import React, {FC} from 'react'
import {Box, Link, Desktop, Text, StyleProps} from '@miro-site/core'
import {useTranslation} from 'next-i18next'
import {Transition} from 'react-transition-group'

import styled from 'styled-components'

import type {CategoryGroup, CategoryGroupItem, CategoryNavProps} from '../CategoryNav'
import {CategoryHeroBanner} from './CategoryHeroBanner'

const SEPARATE_COLUMN_CATEGORIES_LIMIT = 12

function splitCategories(
  resultArray: Array<CategoryGroupItem[]>,
  item: CategoryGroupItem,
  index: number,
) {
  const chunkIndex = Math.floor(index / SEPARATE_COLUMN_CATEGORIES_LIMIT)

  if (!resultArray[chunkIndex]) {
    resultArray[chunkIndex] = []
  }

  resultArray[chunkIndex].push(item)

  return resultArray
}

const transitionStyles = {
  entering: {opacity: 1, visibility: 'visible'},
  entered: {opacity: 1, visibility: 'visible'},
  exiting: {opacity: 0, visibility: 'hidden'},
  exited: {opacity: 0, visibility: 'hidden'},
}

const defaultStyle = {
  textAlign: 'center',
  transition: `opacity 250ms ease-in-out, visibility 250ms ease-in-out`,
  visibility: 'hidden',
  opacity: 0,
}

const getContent = (
  rootGroups: CategoryGroup[],
  onCategoryOpen: (category: CategoryGroupItem) => void,
) => {
  return rootGroups.map((group: CategoryGroup) => (
    <RootGroupWrapper key={group.title}>
      <RootGroupTitle>{group.title}</RootGroupTitle>
      <CategoriesListWrapper>
        {group.categories
          .reduce(splitCategories, [])
          .map((categoryList: CategoryGroupItem[], i) => (
            <Box key={i}>
              {categoryList.map((category: CategoryGroupItem) => (
                <CategoryLink
                  onClick={e => {
                    if (onCategoryOpen) {
                      e.preventDefault()
                      onCategoryOpen(category)
                    }
                  }}
                  key={category.title}
                  href={category.href}
                >
                  {category.title}
                </CategoryLink>
              ))}
            </Box>
          ))}
      </CategoriesListWrapper>
    </RootGroupWrapper>
  ))
}

interface CategoryNavContentProps {
  isVisible?: boolean
  styles?: StyleProps
}

const CategoryNavContent: FC<CategoryNavContentProps> = ({isVisible, styles, children}) => {
  return (
    <Transition in={isVisible} timeout={2500}>
      {state => (
        <Desktop
          $block={true}
          $styles={{
            ...styles,
            ...defaultStyle,
            ...transitionStyles[state],
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 3,
          }}
        >
          <Box
            $styles={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              textAlign: 'center',
            }}
          >
            {children}
          </Box>
        </Desktop>
      )}
    </Transition>
  )
}

const CategoryNavDesktop = React.forwardRef<HTMLElement, CategoryNavProps>(
  ({styles = {}, rootGroups = [], isVisible = false, onCategoryOpen}, ref) => {
    const {t} = useTranslation('common')

    return (
      <CategoryNavContent isVisible={isVisible}>
        <NavWrapperVariant ref={ref}>
          <ContainerWrapper>
            <ListWrapper>{getContent(rootGroups, onCategoryOpen)}</ListWrapper>
            <MiroverseWrapper>
              <RootGroupWrapper key="Miroverse Templates">
                <RootGroupTitle>{t('miroverse-title')}</RootGroupTitle>
                <CategoriesListWrapper>
                  <CategoryHeroBanner />
                </CategoriesListWrapper>
              </RootGroupWrapper>
            </MiroverseWrapper>
          </ContainerWrapper>
        </NavWrapperVariant>
      </CategoryNavContent>
    )
  },
)

CategoryNavDesktop.displayName = 'CategoryNavDesktop'

const ContainerWrapper = styled(Box).attrs({
  $styles: {
    display: 'flex',
  },
})``
const ListWrapper = styled(Box).attrs({
  $styles: {
    flex: 0,
    display: 'inline-flex',
    textAlign: 'left',
    flexFlow: 'row nowrap',
    alignContent: 'flex-start',
  },
})``
const MiroverseWrapper = styled(Box).attrs({
  $styles: {
    flex: 0,
    flexBasis: 220,
    textAlign: 'left',
  },
})``
const commomWrapperLayout = {
  paddingTop: [null, 48, 48, 48],
  paddingBottom: [null, 48, 48, 48],
  paddingLeft: [null, 95, 95, 95],
  paddingRight: [null, 53, 53, 53],
  borderRadius: [null, 16, 16, 16],
  boxShadow: '0px 8px 44px rgba(5, 0, 56, 0.16)',
  backgroundColor: 'primaryWhite',
}

const NavWrapperVariant = styled(Box).attrs(({$styles = {}}) => ({
  $styles: {
    ...commomWrapperLayout,
    ...$styles,
  },
}))({})

const RootGroupWrapper = styled(Box).attrs({
  $styles: {
    marginRight: [null, 28, 28, 28],
  },
})`
  &:last-child {
    margin-right: 0;
  }
`

const RootGroupTitle = styled(Text).attrs(props => ({
  as: 'div',
  $variant: 'p1',
  $styles: {
    fontSize: [null, 12, 12, 12],
    marginBottom: [null, 24, 24, 24],
    fontWeight: 'bold',
    lineHeight: [null, 24, 24, 24],
    letterSpacing: '1px',
    textTransform: 'uppercase',
    color: props.theme.templates.colors.secondary40,
  },
}))({})

const CategoriesListWrapper = styled(Box).attrs({
  $styles: {
    flex: 0,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexDirection: 'row',
    flexWrap: 'nowrap',
  },
})({})

const CategoryLink = styled(Link).attrs({
  $size: 'small',
  $styles: {
    marginRight: [null, 16, 16, 16],
    marginBottom: [null, 8, 8, 8],
    width: [null, 160, 160, 160],
  },
})`
  display: block;
  font-weight: 400;

  border-bottom: none;
  transition: color ${props => props.theme.animation.transitionDuration};

  color: ${props => props.theme.colors.primaryIndigo};

  &:hover {
    color: ${props => props.theme.colors.primaryBlue};
  }

  &:focus {
    ${props => props.theme.normalizeStyles(props.theme.focus.outlines.withOffset)}
  }
`

export default CategoryNavDesktop
